import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
export function About() {
  return <article className="w-full" data-id="element-193">
      {/* Masthead headline */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-id="element-194">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} data-id="element-195">
          <span className="kicker-block text-sm mb-8" data-id="element-196">About Skybox</span>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.9] mt-8" data-id="element-197">
            A media company built for the sports economy.
          </h1>
        </motion.div>
      </section>

      {/* Body */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 lg:pb-24 border-thick-b" data-id="element-198">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.5
      }} className="space-y-8" data-id="element-199">
          <p className="font-sans text-xl sm:text-2xl text-ink leading-relaxed" data-id="element-200">
            We believe the most valuable intelligence in sports, media, and
            entertainment doesn't come from press releases or beat reporters —
            it comes from sitting across from the people actually building,
            buying, and leading these businesses.
          </p>
          <p className="font-sans text-xl sm:text-2xl text-ink leading-relaxed" data-id="element-201">
            Skybox exists to give decision-makers — fund managers, team owners,
            league executives, founders, and operators — direct access that
            creates differentiated insight.
          </p>
        </motion.div>
      </section>

      {/* Under the Number — dark green band */}
      <section className="bg-accent-dark text-cream py-20 lg:py-28 border-thick-b" data-id="element-202">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-203">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-204">
            <span className="kicker-block-inverse text-sm mb-8" data-id="element-205">
              Flagship Property
            </span>
            <h2 className="font-serif text-6xl sm:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.85] mt-8 mb-10" data-id="element-206">
              Under the Number
            </h2>
            <p className="font-sans text-xl sm:text-2xl text-cream/85 leading-relaxed max-w-3xl mx-auto" data-id="element-207">
              A podcast and publication that goes deep with the innovators
              driving the business of sport. Through long-form interviews,
              original essays, and an expanding network of shows, Skybox
              delivers the conversations, research, and frameworks that matter
              to the people shaping the industry.
            </p>
            <Link to="/#podcast" className="inline-flex items-center space-x-3 font-mono text-base font-black uppercase tracking-widest text-cream hover:text-cream/70 transition-colors group border-b-4 border-cream hover:border-cream/70 pb-1 mt-12" data-id="element-208">
              <span data-id="element-209">Listen Now</span>
              <ArrowRight className="w-5 h-5 transform transition-transform group-hover:translate-x-2" data-id="element-210" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Founder */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 border-thick-b" data-id="element-211">
        <span className="font-mono text-sm font-black tracking-widest uppercase text-accent block mb-12" data-id="element-212">
          Founder
        </span>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.5
      }} className="grid grid-cols-1 md:grid-cols-5 gap-10 lg:gap-14 items-start" data-id="element-213">
          {/* Portrait */}
          <div className="md:col-span-2" data-id="element-214">
            <div className="aspect-[4/5] overflow-hidden bg-ink border-4 border-ink" data-id="element-215">
              <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=1000&auto=format&fit=crop" alt="Brent Peus Jr., founder of Skybox" className="w-full h-full object-cover grayscale" data-id="element-216" />
            </div>
          </div>

          {/* Note + signature */}
          <div className="md:col-span-3 flex flex-col" data-id="element-217">
            <p className="font-serif text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-[1.1] mb-8" data-id="element-218">
              Skybox was founded by Brent Peus Jr. in 2025.
            </p>
            <p className="font-sans text-lg sm:text-xl text-ink/80 leading-relaxed mb-10" data-id="element-219">
              It started with a simple conviction: the people building the
              business of sport deserve a media company that takes them as
              seriously as they take the game. We built Skybox to be in the room
              — and to bring you with us.
            </p>

            <div className="mt-auto" data-id="element-220">
              <span aria-hidden="true" className="font-signature text-6xl sm:text-7xl text-ink leading-none block -mb-2" data-id="element-221">
                Brent Peus Jr.
              </span>
              <div className="border-t-2 border-ink pt-4 mt-4 inline-block" data-id="element-222">
                <p className="font-mono text-sm font-black uppercase tracking-widest text-ink" data-id="element-223">
                  Brent Peus Jr.
                </p>
                <p className="font-mono text-xs font-bold uppercase tracking-widest text-ink/60 mt-1" data-id="element-224">
                  Founder, Skybox
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </section>
    </article>;
}